<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
      <div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Top Attendees</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Attendees with most bookings</p>
      </div>
      <span v-if="range" class="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-lg">
        {{ range }}
      </span>
    </div>

    <!-- Skeleton -->
    <div v-if="loading && !data" class="animate-pulse p-4 space-y-3">
      <div v-for="i in 4" :key="i" class="flex items-center gap-3">
        <div class="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
        <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
        <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-6"></div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
        </svg>
      </div>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">No attendees yet</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Top bookers will appear here.</p>
    </div>

    <!-- List -->
    <div v-else class="divide-y divide-gray-50 dark:divide-gray-800/60">
      <div class="flex items-center justify-between px-5 py-2.5 bg-gray-50 dark:bg-gray-800/40">
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Attendee</span>
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bookings</span>
      </div>
      <div v-for="attendee in stats" :key="attendee.attendee_name"
        class="flex items-center gap-3 px-5 py-3">
        <div class="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
          <span class="text-xs font-semibold text-gray-600 dark:text-gray-300 leading-none"
            v-html="attendee.attendee_avatar_name"></span>
        </div>
        <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate flex-1">{{ attendee.attendee_name }}</span>
        <span class="text-sm font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0">{{ attendee.bookings_count }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAnalyticsBookingsStore } from 'stores/analyticsBookings.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsBookingsStore()

const loading = computed(() => store.topAttendeesLoading)
const data    = computed(() => store.topAttendees[props.duration] ?? null)
const range   = computed(() => data.value?.range ?? null)
const stats   = computed(() => (data.value?.stats ?? []).slice().sort((a, b) => b.bookings_count - a.bookings_count))
const isEmpty = computed(() => Array.isArray(data.value?.stats) && data.value.stats.length === 0)
</script>
